<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { useAppointmentFetcher } from '@satellite/composables/useAppointmentFetcher';
import { useAppointmentDisplay } from '@satellite/composables/useAppointmentDisplay';
import AppointmentDisplay from '@satellite/components/elements/presenters/AppointmentDisplay.vue';

import type { Maybe } from '@nova/core';
import type { AppointmentDetails } from '@satellite/composables/appointmentPresenterShared';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { AppointmentDisplayField } from '@satellite/composables/useAppointmentDisplay';

const {
  size = 'regular',
  orientation = 'vertical',
  appointmentId = null,
  initialAppointmentData = null,
  placeholderAppointmentData = null,
  staticAppointmentData = null,
  mode = EntityPresenterMode.Live,
  warehouseTimezone = null,
  displayFields = ['confirmationNumber'],
  carrierCompanyProps = {},
  confirmationNumberProps = {},
  dateProps = {},
  endProps = {},
  refNumbersProps = {},
  startProps = {},
  statusProps = {},
  timeIntervalProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  orientation?: 'horizontal' | 'vertical';
  appointmentId?: Maybe<string>;
  initialAppointmentData?: Maybe<AppointmentDetails>;
  placeholderAppointmentData?: Maybe<PresenterPlaceholderData<AppointmentDetails>>;
  staticAppointmentData?: Maybe<AppointmentDetails>;
  mode?: EntityPresenterMode;
  warehouseTimezone?: Maybe<string>;
  displayFields?: AppointmentDisplayField[];
  carrierCompanyProps?: Record<string, unknown>;
  confirmationNumberProps?: Record<string, unknown>;
  dateProps?: Record<string, unknown>;
  endProps?: Record<string, unknown>;
  refNumbersProps?: Record<string, unknown>;
  startProps?: Record<string, unknown>;
  statusProps?: Record<string, unknown>;
  timeIntervalProps?: Record<string, unknown>;
}>();

const {
  data: appointment,
  isLoading,
  isFetching,
  onIntersect
} = useAppointmentFetcher({
  appointmentId: computed(() => appointmentId),
  initialAppointmentData: computed(() => initialAppointmentData),
  placeholderAppointmentData: computed(() => placeholderAppointmentData),
  staticAppointmentData: computed(() => staticAppointmentData),
  mode
});

const data = useAppointmentDisplay(appointment, {
  size: computed(() => size),
  warehouseTimezone: computed(() => warehouseTimezone),
  mode: computed(() => mode),
  isFetching,
  carrierCompanyProps: computed(() => carrierCompanyProps),
  confirmationNumberProps: computed(() => confirmationNumberProps),
  dateProps: computed(() => dateProps),
  endProps: computed(() => endProps),
  refNumbersProps: computed(() => refNumbersProps),
  startProps: computed(() => startProps),
  statusProps: computed(() => statusProps),
  timeIntervalProps: computed(() => timeIntervalProps)
});
</script>

<template>
  <div
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.25]
      }
    }">
    <v-skeleton-loader
      :loading="isLoading"
      type="text"
      class="appointment-presenter-v2 bg-transparent">
      <appointment-display
        :data="data"
        :display-fields="displayFields"
        :orientation="orientation"
        :style="isLoading ? 'min-width: 50px' : ''">
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
          <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>
      </appointment-display>
    </v-skeleton-loader>
  </div>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 0 !important;
  height: 1em !important;
}
</style>
